import { Injectable } from '@angular/core';
import { WindowReferenceService } from './window-reference-service.service';
import { MobileDelegateService } from './mobile-delegate.service';
import { DelegateType } from '../../shared/models/utility.model';
import { getMobileAppVersion, isMoniepointAppUser } from '../../shared/models/mobile-app-helper.model';
import { EventName, initializeDatalayer } from '../../shared/models/data.layer.model';
import { AppConfig } from '../../shared/config/app-config';
import { CustomerIOEventTrackingService } from './customer-io.service';
import { LocationHelper } from '../helpers/location.helper';
import { LocalStorageService } from './local-storage.service';
import { StorageKeys } from '@mpt-fo/common/shared/models/storage-keys';
import { IdentityProvider } from '../../kamona/model/kamonal-model';
import { logger } from '@mpt-fo/common/telemetry';

@Injectable({
  providedIn: 'root',
})
export class DataLayerService {
  private window;

  constructor(
    windowRef: WindowReferenceService,
    public customerIOService: CustomerIOEventTrackingService,
    public mobileDelegate: MobileDelegateService,
    public config: AppConfig,
    private locationHelper: LocationHelper,
    private localStorageService: LocalStorageService
  ) {
    this.window = windowRef.nativeWindow; // initialize the window to what we get from our window service
  }

  logEventWithData(eventName: string, eventData?: any) {
    const datalayer = initializeDatalayer(eventName);
    this.logEvent({ ...datalayer, ...eventData });
  }

  identifyUser() {
    this.logEventWithData(EventName.identify_user, {});
  }

  resetUser() {
    this.pushEvent({ event: EventName.reset_user });
  }

  logPageEvent() {
    this.pushEvent({
      event: EventName.page_change,
    });
  }

  logEvent(payload: any) {
    this.updateMixPanelPayloadWithEnvironmentVariable(payload);
    this.updateMixpanelPayloadIfIdentityUserEvent(payload);
    this.pushEvent(payload);
    this.logMobileEvent(payload);
  }

  updateMixPanelPayloadWithEnvironmentVariable(payload: any) {
    if (!payload) return;

    payload.env = this.config.FO_ENVIRONMENT;
    payload.app_env = this.config.FO_ENVIRONMENT;
  }

  updateMixpanelPayloadIfIdentityUserEvent(payload: any) {
    if (payload.event !== 'identify_user') {
      return;
    }
    payload = Object.keys(payload).reduce((acc, key) => {
      if (payload[key] !== '') {
        acc[key] = payload[key];
      }
      return acc;
    }, {});
  }

  getAdjustEvent(event: string) {
    return AdjustEventMap.get(event);
  }


  async buildTransactionDataLayerPayload(event: string, amount?: number, payment_type?: string) {
    const payload = {
      ...initializeDatalayer(event),
      transaction_amount: amount,
      event: event,
      user_name: this.localStorageService.get(StorageKeys.username),
      email: this.localStorageService.get(StorageKeys.email),
      payment_type: payment_type,
      onboarding_mode: this.localStorageService.get(StorageKeys.on_boarding_mode) || 'BRM',
    };

    if (!this.config.ENABLE_LOCATION_DURING_PAYMENT) {
      return payload
    }

    const geoHeaders = await this.locationHelper.getCoordinates();

    if (geoHeaders) {
      payload.longitude = geoHeaders.longitude;
      payload.latitude = geoHeaders.latitude;
    }

    return payload;
  }

  async buildOnboardingDataLayerPayload(event: string, errorDetails?: { errorCode?: string; errorMessage: string; }) {
    const payload = {
      ...initializeDatalayer(event),
      auth_type_provider: IdentityProvider.SMILE_ID,
    }

    if(errorDetails) {
      payload.api_error_code = errorDetails.errorCode,
      payload.api_error_message = errorDetails.errorMessage,
      payload.error_message = errorDetails.errorMessage
    }

    try {
      const hasLocationPermission = await this.locationHelper.hasPermission()
      const geoHeaders = hasLocationPermission && await this.locationHelper.getCoordinates();

      if(geoHeaders) {
        payload.longitude = geoHeaders.longitude
        payload.latitude = geoHeaders.latitude
      }
    }
    catch (err){
      logger.error(JSON.stringify(err))
    }

    return payload;
  }

  private logMobileEvent(payload: any) {
    if (!isMoniepointAppUser()) {
      return;
    }
    const adjustEvent = this.getAdjustEvent(payload?.event);
    if (!adjustEvent) {
      return;
    }
    payload.event = adjustEvent;
    payload.mobile_version = getMobileAppVersion();
    this.mobileDelegate
      .setDelegateData(DelegateType.adjustAddCallbackParameter, JSON.stringify(payload))
      .then((res) => {
        console.log('adjustAddCallbackParameter successfully ---> ', res);
      })
      .catch((error) => {
        console.log('Error from adjustAddCallbackParameter ---> ', error);
      });
  }

  private pushEvent(obj: any) {
    if (obj) {
      this.window.dataLayer.push(obj);
    }
  }
}

export const AdjustEventMap = new Map([
  ['sign_up_initiated', 'd7m18t'],
  ['sign_up_success', 'veizpn'],
  ['signup_account_creation_initiated', '3ka6yv'],
  ['account_number_created', 'x85r4u'],
  ['payment_initiated', '8wxw5t'],
  ['payment_success', 'mplfp2'],
  ['terminal_request_initiated', '9085d6'],
  ['terminal_request_success', 'birpbd'],
  ['sign_in_success', 'ewqpdc'],
]);

export const AuthenticationEvents = {
  initiated: 'authentication_intiated',
  success: 'authentication_success',
  failed: 'authentication_failed',
};

export const LocationRequestEvents = {
  initiated: 'location_permission_initiated',
  success: 'location_permission_success',
  failed: 'location_permission_failure'
}

export const StorefrontUploadEvents = {
  initiated: 'storefront_image_upload_initiated',
  success: 'storefront_image_upload_success',
  failed: 'storefront_image_upload_failed'
}

export const SignUpLivelinessEvents = {
  initiated: 'signup_liveliness_check_initiated',
  success: 'signup_liveliness_check_success',
  failed: 'signup_liveliness_check_failed'
}

export const Authentication2FAMixpanelSteps = {
  GENERATE_OTP_STARTED: 'GENERATE_OTP_STARTED',
  GENERATE_OTP_FAILED: 'GENERATE_OTP_FAILED',
  GENERATE_OTP_SUCCESS: 'GENERATE_OTP_SUCCESS',
  VALIDATE_OTP_STARTED: 'VALIDATE_OTP_STARTED',
  VALIDATE_OTP_FAILED: 'VALIDATE_OTP_FAILED',
  VALIDATE_OTP_SUCCESS: 'VALIDATE_OTP_SUCCESS',
  INITIATE_FACE_MATCH_STARTED: 'INITIATE_FACE_MATCH_STARTED',
  INITIATE_FACE_MATCH_FAILED: 'INITIATE_FACE_MATCH_FAILED',
  INITIATE_FACE_MATCH_SUCCESS: 'INITIATE_FACE_MATCH_SUCCESS',
  VALIDATE_FACE_MATCH_STARTED: 'VALIDATE_FACE_MATCH_STARTED',
  VALIDATE_FACE_MATCH_FAILED: 'VALIDATE_FACE_MATCH_FAILED',
  VALIDATE_FACE_MATCH_SUCCESS: 'VALIDATE_FACE_MATCH_SUCCESS',
  DEVICE_ACTIVATION_SUCCESS: 'DEVICE_ACTIVATION_SUCCESS',
  DEVICE_ACTIVATION_FAILED: 'DEVICE_ACTIVATED',
};
